import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useAuthStore } from '@/store/authStore'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const sentryEnabled = Boolean(sentryDsn)

export const initSentry = () => {
  if (!sentryEnabled) return

  const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0')

  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_NODE_ENV || import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0,
  })
}

export const SentryUserContextSync = () => {
  const tenantId = useAuthStore((s) => s.tenantId)
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    if (!sentryEnabled) return

    if (tenantId) {
      Sentry.setTag('tenantId', tenantId)
    } else {
      Sentry.setTag('tenantId', 'unknown')
    }

    if (user?.id || user?.email) {
      Sentry.setUser({
        id: user?.id,
        email: user?.email,
        username: user?.name,
      })
    } else {
      Sentry.setUser(null)
    }
  }, [tenantId, user?.id, user?.email, user?.name])

  return null
}

export const AppErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  if (!sentryEnabled) return <>{children}</>

  return (
    <Sentry.ErrorBoundary
      showDialog={false}
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
            <h1 className="text-lg font-semibold text-gray-900">Ocurrió un error inesperado</h1>
            <p className="text-sm text-gray-600 mt-2">
              El incidente fue reportado automáticamente. Recarga la página para continuar.
            </p>
          </div>
        </div>
      }
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}
